/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { switchMap, tap } from 'rxjs';

import { TaxRule, UnitOfCost } from '~core/models/tax-rules.models';
import { Money, Product, Tax } from '~core/models/core.models';
import {
  BillingType,
  ComputationLineItem,
  ComputationProduct,
  ComputationTaxRule,
  Invoice,
  LineItem
} from '~core/models/invoice.models';
import { ValidationComputationParams } from '~core/models/validation-transaction.models';
import { getInvoicePaymentMethods } from '~core/utils/payment';
import { toISOUTCWithOffset } from './date.utils';

type LineItemsFormValue = Partial<{
  designation: string | Product | null;
  reference: string | null;
  category: string | null;
  baseUnitId: string | null;
  quantity: number | null;
  unitPrice: number | null;
  taxes: TaxRule[] | null;
  discountCost: number | Money | null;
  discountUnitOfCost: UnitOfCost | null;
}>;

export const convertToTaxRule = (
  taxRules: TaxRule[],
  taxes?: Tax[]
): TaxRule[] => {
  if (!taxes?.length) return [];
  return taxRules.filter(rule => taxes.some(tax => tax.id === rule.id));
};

/* export const buildLineItem = (value: LineItemsFormValue): LineItem => {
  console.log('formValue ss', value);

  return {
    article: {
      ...value.article,
      unitPrice: {
        amount: value.unitPrice,
        currency:
          value.article?.unitPrice?.currency || value.article?.price?.currency
      }
    } as Product,
    quantity: value.quantity,
    discountUnitOfCost: value.discountUnitOfCost,
    discount: value.discountCost,
    unitMeasure: value.unitMeasure,
    taxesApplied: value.taxes
      ? value.taxes?.map((tax: TaxRule) => ({ code: tax.code }))
      : value.taxesApplied?.map((tax: TaxRule) => ({ code: tax.code }))
  } as LineItem;
}; */

export const buildLineItem = (
  value: any,
  currentLineItem?: LineItem
): ComputationLineItem => {
  const { designation, reference, category, ...restValue } = value;

  const computationLineItem = {
    ...restValue,
    article: {
      name: designation,
      code: reference,
      category,
      price: { amount: restValue.unitPrice }
    } as ComputationProduct,

    unitMeasure: currentLineItem
      ? currentLineItem.unitMeasure
      : restValue.baseUnitId,
    taxesApplied: restValue.taxes?.map((tax: TaxRule) => ({
      code: tax.code,
      label: tax.label,
      unitOfCost: tax.unitOfCost,
      cost: tax.cost
    }))
  };

  delete computationLineItem.taxes;
  delete computationLineItem.baseUnitId;
  delete computationLineItem.unitPrice;

  return computationLineItem as ComputationLineItem;
};

export const buildComputationParams = (
  value: LineItem[] | LineItemsFormValue,
  invoice: Invoice,
  currenLineItem?: LineItem
): ValidationComputationParams => {
  const customer = invoice.customer;
  const supplier = invoice.supplier;

  const lineItems = Array.isArray(value)
    ? value.map(stripSystemFields)
    : [buildLineItem(stripSystemFields(value), currenLineItem)];

  return {
    lineItems: lineItems,
    issuedAt: new Date(invoice.issuedAt)
      .toISOString()
      .replace('.000Z', '+00:00'),
    paymentMethods: getInvoicePaymentMethods(invoice),
    taxesApplied: removeKindToTaxApplied(invoice.taxesApplied),
    typeDocument: invoice.type,
    billingType: BillingType.bToB,
    customer: {
      name: customer.name,
      billingType: customer.billingType,
      country: customer.country,
      city: customer.city,
      phoneNumber: customer.phoneNumber,
      location: customer.location
    },
    supplier: {
      name: supplier.name,
      nif: supplier.nif,
      organizationId: supplier.id,
      taxSystem: supplier.taxSystem,
      billingType: supplier.billingType,
      country: supplier.country,
      city: supplier.city,
      phoneNumber: supplier.phoneNumber,
      location: supplier.location
    }
  };
};

export const removeKindTolineItemsTaxesApplied = (lineItems: LineItem[]) =>
  lineItems.map(lineItem => {
    const { createdAt, updatedAt, ...rest } = lineItem;
    return {
      ...rest,
      taxesApplied: removeKindToTaxApplied(lineItem.taxesApplied)
    };
  });

export const removeKindToTaxApplied = (
  taxesApplied: ComputationTaxRule[] = []
) =>
  taxesApplied.map(taxApplied => {
    const { kind, ...rest } = taxApplied;
    return rest;
  });

export const formatTaxesApplied = (
  taxes: ComputationTaxRule[]
): { code: string }[] => {
  return taxes.map(tax => ({ code: tax.code }));
};

export const formatLineItems = (lineItems: LineItem[] = []) => {
  return lineItems.map(({ createdAt, updatedAt, ...lineItemRest }) => ({
    ...lineItemRest,
    taxesApplied: lineItemRest.taxesApplied?.map(
      ({ kind, ...taxRest }) => taxRest
    )
  }));
};

const stripSystemFields = (obj: any): any => {
  const { id, createdAt, updatedAt, ...rest } = obj || {};
  return rest;
};

const isLineItem = (obj: any): boolean => {
  return obj.unitMeasure;
};

export const computeAndUpdateInvoice = (params: {
  invoice: Invoice;
  validationService: any;
  invoiceService: any;
  invoiceFacade: any;
  destroyRef: any;
  additionalComputeParams?: Record<string, any>;
  onSuccess?: () => void;
}) => {
  const {
    invoice,
    validationService,
    invoiceService,
    invoiceFacade,
    destroyRef,
    additionalComputeParams = {},
    onSuccess
  } = params;

  const computePayload = {
    lineItems: formatLineItems(invoice.lineItems),
    taxesApplied: formatTaxesApplied(
      invoice.taxesApplied as ComputationTaxRule[]
    ),
    paymentMethods: getInvoicePaymentMethods(invoice),
    typeDocument: invoice.type,
    customer: invoice.customer,
    supplier: invoice.supplier,
    billingType: invoice.billingType,
    issuedAt: invoice.issuedAt,
    ...additionalComputeParams
  } as ValidationComputationParams;

  return validationService.compute(computePayload).pipe(
    switchMap((computationResults: any) => {
      const updateInvoicePayload = {
        amountBeforeTax: computationResults.amountBeforeTax,
        taxesApplied: computationResults.taxesApplied,
        totalAmount: computationResults.totalAmount,
        ...(computationResults.discount && {
          discount: computationResults.discount
        }),
        ...(computePayload.discountUnitOfCost && {
          discountUnitOfCost: computePayload.discountUnitOfCost
        }),
        ...(computePayload.discountCost && {
          discountCost: computePayload.discountCost
        })
      };

      return invoiceService.update(invoice.id as string, updateInvoicePayload);
    }),
    tap((result: any) => {
      const invoicePayload = {
        ...result,
        issuedAt: toISOUTCWithOffset(result.issuedAt as string),
        dueDate: toISOUTCWithOffset(result.dueDate as string)
      };

      invoiceFacade.patchDraftInvoice(invoicePayload as Partial<Invoice>);

      if (onSuccess) {
        onSuccess();
      }
    })
  );
};

export const calculateInvoiceTotalTVA = (invoice?: Invoice): number => {
  if (!invoice) return 0;

  const isTVA = (t: any): boolean => t.kind?.toUpperCase() === 'TVA';

  const linesTaxes =
    invoice.lineItems?.reduce((acc: number, line: LineItem) => {
      const lineTax =
        line.taxesApplied
          ?.filter(isTVA)
          .reduce(
            (sum: number, tax) => sum + (tax.totalAmount?.amount || 0),
            0
          ) || 0;
      return acc + lineTax;
    }, 0) || 0;

  const invoiceTaxes =
    invoice.taxesApplied
      ?.filter(isTVA)
      .reduce((acc: number, tax) => acc + (tax.totalAmount?.amount || 0), 0) ||
    0;

  return linesTaxes + invoiceTaxes;
};

export const calculateInvoiceOtherTaxes = (
  invoice?: Invoice
): { label: string; amount: number }[] => {
  if (!invoice) return [];

  const taxMap = new Map<string, number>();
  const isNotTVA = (rule: any) => rule.kind?.toUpperCase() !== 'TVA';

  const addTax = (label: string, amount: number) => {
    let finalLabel = label;
    const upperLabel = label.toUpperCase();
    if (
      upperLabel === 'TIMBRE' ||
      upperLabel === 'STAMP' ||
      upperLabel === 'TIMBRE_FISCAL'
    ) {
      finalLabel = 'Timbre de quittance';
    }
    taxMap.set(finalLabel, (taxMap.get(finalLabel) || 0) + amount);
  };

  invoice.lineItems?.forEach(line => {
    line.taxesApplied?.filter(isNotTVA).forEach(tax => {
      if (tax.kind && tax.totalAmount?.amount) {
        addTax(tax.kind, tax.totalAmount.amount);
      }
    });
  });

  invoice.taxesApplied?.filter(isNotTVA).forEach(tax => {
    if (tax.kind && tax.totalAmount?.amount) {
      addTax(tax.kind, tax.totalAmount.amount);
    }
  });

  return Array.from(taxMap.entries()).map(([label, amount]) => ({
    label,
    amount
  }));
};

export const shouldShowCurrencyToggle = (
  invoice?: Invoice,
  defaultCurrency: string = 'XOF'
): boolean => {
  if (!invoice) return false;

  const billingCurrency = invoice.customer.billingCurrency;
  return (
    invoice.billingType === 'BtoF' &&
    !!billingCurrency &&
    billingCurrency !== defaultCurrency
  );
};

export const getDisplayedCurrency = (
  invoice?: Invoice,
  mode: 'local' | 'foreign' = 'local',
  defaultCurrency: string = 'XOF'
): string => {
  if (mode === 'local') {
    return defaultCurrency;
  }
  return invoice?.customer.billingCurrency || defaultCurrency;
};

export const getAmountWithCurrency = (
  amountXof?: number,
  amountForeign?: number,
  mode: 'local' | 'foreign' = 'local'
): number => {
  if (mode === 'local') {
    return amountXof ?? 0;
  }

  return amountForeign ?? 0;
};
